import { FALLBACK_LOGO_URL } from "@/lib/avatar";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Link } from "react-router-dom";

interface EventItem {
  id: number;
  slug: string;
  title: string;
  caption: string;
  date: string;
  location: string;
  main_image: string;
  description: string;
  full_content: string;
  status: string;
  gallery_images: string[];
  gallery_videos: string[];
  created_at: string;
}

interface Toast {
  id: number;
  type: "success" | "error";
  message: string;
}

const emptyForm = {
  slug: "",
  title: "",
  caption: "",
  date: "",
  location: "",
  main_image: "",
  description: "",
  full_content: "",
  status: "active" as "active" | "inactive",
  gallery_images: [] as string[],
  gallery_videos: [] as string[],
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.toString().split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function isVideoUrl(url: string): boolean {
  const videoExtensions = ['.mp4', '.webm', '.mov', '.mkv', '.avi'];
  const lower = url.toLowerCase();
  return videoExtensions.some((ext) => lower.includes(ext)) ||
    lower.includes('youtube.com') || lower.includes('youtu.be') || lower.includes('vimeo.com');
}

export default function EventManager() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  let toastCounter = 0;
  function showToast(type: "success" | "error", message: string) {
    const id = Date.now() + toastCounter++;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 6000);
  }

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setLoading(true);
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) {
      setEvents(data.map((ev) => ({
        ...ev,
        gallery_images: ev.gallery_images || [],
        gallery_videos: ev.gallery_videos || [],
      })));
    }
    setLoading(false);
  }

  function openCreate() {
    setForm(emptyForm);
    setIsEditing(false);
    setEditId(null);
    setShowForm(true);
    setNewGalleryUrl("");
    setNewVideoUrl("");
  }

  function openEdit(event: EventItem) {
    setForm({
      slug: event.slug,
      title: event.title,
      caption: event.caption,
      date: event.date || "",
      location: event.location || "",
      main_image: event.main_image,
      description: event.description || "",
      full_content: event.full_content || "",
      status: event.status as "active" | "inactive",
      gallery_images: event.gallery_images || [],
      gallery_videos: event.gallery_videos || [],
    });
    setIsEditing(true);
    setEditId(event.id);
    setShowForm(true);
    setNewGalleryUrl("");
    setNewVideoUrl("");
  }

  function closeForm() {
    setShowForm(false);
    setIsEditing(false);
    setEditId(null);
    setForm(emptyForm);
    setNewGalleryUrl("");
    setNewVideoUrl("");
  }

  async function ensureBucketExists() {
    try {
      const { data: buckets } = await supabase.storage.listBuckets();
      const exists = buckets?.some((b) => b.name === "event-images");
      if (!exists) {
        await supabase.storage.createBucket("event-images", {
          public: true,
          fileSizeLimit: 52428800,
          allowedMimeTypes: null,
        });
      } else {
        // Atualiza bucket para aceitar qualquer tipo de arquivo
        await supabase.storage.updateBucket("event-images", {
          public: true,
          fileSizeLimit: 52428800,
          allowedMimeTypes: null,
        });
      }
    } catch {
      // ignorar erro de permissão — bucket pode já existir
    }
  }

  async function uploadImage(file: File): Promise<string | null> {
    if (!file) return null;
    try {
      setUploading(true);
      await ensureBucketExists();

      const filePath = `events/${Date.now()}-${file.name.replace(/\s+/g, "_")}`;

      const { error: uploadError } = await supabase.storage
        .from("event-images")
        .upload(filePath, file, {
          contentType: file.type || "image/jpeg",
          upsert: false,
        });

      if (uploadError) {
        showToast("error", "Falha no upload: " + uploadError.message);
        return null;
      }

      const { data: publicUrlData } = supabase.storage
        .from("event-images")
        .getPublicUrl(filePath);

      return publicUrlData.publicUrl;
    } catch (err: any) {
      showToast("error", "Erro ao enviar arquivo: " + (err?.message || "Erro desconhecido"));
      return null;
    } finally {
      setUploading(false);
    }
  }

  async function uploadViaEdgeFunction(file: File): Promise<string | null> {
    if (!file) return null;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || "";

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upload-event-image`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch {
        result = { error: responseText };
      }

      if (!response.ok || !result.success) {
        showToast("error", "Falha no upload: " + (result.error || "Erro desconhecido"));
        return null;
      }

      return result.url || null;
    } catch (err: any) {
      showToast("error", "Erro ao enviar arquivo: " + (err?.message || "Erro desconhecido"));
      return null;
    } finally {
      setUploading(false);
    }
  }

  async function handleMainImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setForm((f) => ({ ...f, main_image: url }));
      showToast("success", "Imagem principal enviada!");
    }
  }

  async function handleGalleryUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const url = await uploadImage(files[i]);
      if (url) {
        setForm((f) => ({ ...f, gallery_images: [...f.gallery_images, url] }));
        successCount++;
      }
    }
    if (successCount > 0) {
      showToast("success", `${successCount} imagem(ns) adicionada(s) à galeria!`);
    }
    e.target.value = "";
  }

  function addGalleryImage() {
    const url = newGalleryUrl.trim();
    if (!url) {
      showToast("error", "Cole uma URL de imagem válida.");
      return;
    }
    if (form.gallery_images.includes(url)) {
      showToast("error", "Esta imagem já foi adicionada.");
      return;
    }
    setForm((f) => ({ ...f, gallery_images: [...f.gallery_images, url] }));
    setNewGalleryUrl("");
  }

  function removeGalleryImage(index: number) {
    setForm((f) => ({
      ...f,
      gallery_images: f.gallery_images.filter((_, i) => i !== index),
    }));
  }

  function addVideoUrl() {
    const url = newVideoUrl.trim();
    if (!url) {
      showToast("error", "Cole uma URL de vídeo válida.");
      return;
    }
    if (form.gallery_videos.includes(url)) {
      showToast("error", "Este vídeo já foi adicionado.");
      return;
    }
    setForm((f) => ({ ...f, gallery_videos: [...f.gallery_videos, url] }));
    setNewVideoUrl("");
  }

  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const url = await uploadViaEdgeFunction(files[i]);
      if (url) {
        setForm((f) => ({ ...f, gallery_videos: [...f.gallery_videos, url] }));
        successCount++;
      }
    }
    if (successCount > 0) {
      showToast("success", `${successCount} vídeo(s) adicionado(s) à galeria!`);
    }
    e.target.value = "";
  }

  function removeVideoUrl(index: number) {
    setForm((f) => ({
      ...f,
      gallery_videos: f.gallery_videos.filter((_, i) => i !== index),
    }));
  }

  function copyEventUrl(slug: string, id: number) {
    const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
    const pathPrefix = basePath ? `/${basePath}` : "";
    const fullUrl = `${window.location.origin}${pathPrefix}/evento/${slug}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedId(id);
      showToast("success", "Link copiado!");
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      showToast("error", "Não foi possível copiar o link.");
    });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form.slug.trim() || !form.title.trim() || !form.main_image.trim() || !form.caption.trim()) {
      showToast("error", "Preencha todos os campos obrigatórios: slug, título, legenda e imagem.");
      return;
    }

    const payload = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      caption: form.caption.trim(),
      date: form.date.trim() || null,
      location: form.location.trim() || null,
      main_image: form.main_image.trim(),
      description: form.description.trim() || null,
      full_content: form.full_content.trim() || null,
      status: form.status,
      gallery_images: form.gallery_images,
      gallery_videos: form.gallery_videos,
    };

    if (isEditing && editId) {
      const { error } = await supabase
        .from("events")
        .update(payload)
        .eq("id", editId);
      if (!error) {
        showToast("success", "Evento atualizado com sucesso!");
        loadEvents();
        closeForm();
      } else {
        showToast("error", "Erro ao atualizar evento: " + error.message);
      }
    } else {
      const { error } = await supabase
        .from("events")
        .insert(payload);
      if (!error) {
        showToast("success", "Evento criado com sucesso!");
        loadEvents();
        closeForm();
      } else {
        showToast("error", "Erro ao criar evento: " + error.message);
      }
    }
  }

  async function toggleStatus(id: number, current: string) {
    const newStatus = current === "active" ? "inactive" : "active";
    const { error } = await supabase
      .from("events")
      .update({ status: newStatus })
      .eq("id", id);
    if (!error) {
      setEvents((prev) =>
        prev.map((ev) => (ev.id === id ? { ...ev, status: newStatus } : ev))
      );
      showToast("success", `Evento ${newStatus === "active" ? "ativado" : "desativado"}.`);
    }
  }

  async function deleteEvent(id: number) {
    if (!confirm("Tem certeza que deseja excluir este evento permanentemente?")) return;
    const { error } = await supabase.from("events").delete().eq("id", id);
    if (!error) {
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
      showToast("success", "Evento excluído.");
    } else {
      showToast("error", "Erro ao excluir evento.");
    }
  }

  return (
    <div>
      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed top-20 right-6 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`px-4 py-3 rounded-xl shadow-lg font-body text-sm flex items-start gap-2.5 animate-slide-in ${
                t.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              <i className={`${t.type === "success" ? "ri-checkbox-circle-line text-green-500" : "ri-error-warning-line text-red-500"} mt-0.5`} />
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="text-brown/30 hover:text-brown/60 cursor-pointer shrink-0"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Uploading overlay */}
      {uploading && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="font-body text-sm text-brown">Enviando arquivo...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-display text-xl text-brown">Eventos</h3>
          <p className="font-body text-sm text-brown/60 mt-1">Gerencie os eventos da seção "Nossa História"</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" /> Novo evento
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl p-6 mb-6">
          <h4 className="font-display text-lg text-brown mb-4">
            {isEditing ? "Editar Evento" : "Novo Evento"}
          </h4>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Título *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ex: Encontro de Acolhimento"
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="ex: encontro-acolhimento-2025"
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Legenda *
                </label>
                <input
                  type="text"
                  value={form.caption}
                  onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ex: Encontro de acolhimento — junho 2025"
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Data
                </label>
                <input
                  type="text"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ex: junho 2025"
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Local
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ex: Boca Raton, FL"
                />
              </div>
              <div>
                <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as "active" | "inactive" }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>
            </div>

            {/* Main Image Upload */}
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Imagem Principal (capa) *
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={form.main_image}
                    onChange={(e) => setForm((f) => ({ ...f, main_image: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                    placeholder="URL da imagem..."
                  />
                </div>
                <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-cloud-line" />
                  <span>Enviar arquivo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    className="hidden"
                  />
                </label>
              </div>
              {form.main_image && (
                <div className="mt-3 w-32 h-32 rounded-xl overflow-hidden">
                  <img
                    src={form.main_image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Descrição curta (resumo para a galeria)
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Resumo do evento para a galeria..."
              />
              <p className="font-body text-[11px] text-brown/40 mt-1 text-right">{form.description.length}/500</p>
            </div>
            <div>
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Conteúdo completo (para a página de detalhes)
              </label>
              <textarea
                value={form.full_content}
                onChange={(e) => setForm((f) => ({ ...f, full_content: e.target.value }))}
                rows={8}
                maxLength={5000}
                className="w-full px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors resize-none"
                placeholder="Conteúdo completo da página de detalhes..."
              />
              <p className="font-body text-[11px] text-brown/40 mt-1 text-right">{form.full_content.length}/5000</p>
            </div>

            {/* Gallery Images Section */}
            <div className="pt-2">
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Galeria de Fotos
              </label>
              <p className="font-body text-xs text-brown/50 mb-3">
                Adicione fotos extras para a galeria do evento. Você pode enviar arquivos ou colar URLs.
              </p>

              {/* Upload files */}
              <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors cursor-pointer mb-3 w-fit">
                <i className="ri-upload-cloud-line" />
                <span>Enviar fotos da galeria</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  className="hidden"
                />
              </label>

              {/* Add by URL */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newGalleryUrl}
                  onChange={(e) => setNewGalleryUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGalleryImage())}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ou cole a URL da imagem..."
                />
                <button
                  type="button"
                  onClick={addGalleryImage}
                  className="px-4 py-2.5 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line" /> Adicionar
                </button>
              </div>
              {form.gallery_images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {form.gallery_images.map((url, idx) => (
                    <div key={idx} className="relative group rounded-lg overflow-hidden aspect-square border border-nude/50 hover:border-red-300 transition-colors">
                      <img
                        src={url}
                        alt={`Galeria ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = FALLBACK_LOGO_URL;
                        }}
                      />
                      {/* Overlay escuro sutil no hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
                      {/* Botão de deletar - sempre visível */}
                      <button
                        type="button"
                        onClick={async () => {
                          // Tenta deletar do Storage se for URL do Supabase
                          if (url.includes('event-images')) {
                            try {
                              const urlObj = new URL(url);
                              const pathMatch = urlObj.pathname.match(/\/storage\/v1\/object\/public\/event-images\/(.+)/);
                              if (pathMatch) {
                                await supabase.storage.from('event-images').remove([pathMatch[1]]);
                              }
                            } catch { /* ignora erro de deleção do storage */ }
                          }
                          removeGalleryImage(idx);
                        }}
                        className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-full bg-white/85 hover:bg-red-500 text-brown/60 hover:text-white transition-all cursor-pointer shadow-sm z-10"
                        title="Remover foto"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                      {/* Número da foto - sempre visível */}
                      <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white font-body text-[10px] z-10">
                        {idx + 1}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Gallery Videos Section */}
            <div className="pt-2">
              <label className="font-body text-xs text-brown/60 uppercase tracking-wide block mb-1.5">
                Vídeos da Galeria
              </label>
              <p className="font-body text-xs text-brown/50 mb-3">
                Adicione vídeos para a galeria do evento. Você pode enviar arquivos do computador ou colar URLs (YouTube, Vimeo).
              </p>

              {/* Upload video files */}
              <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors cursor-pointer mb-3 w-fit">
                <i className="ri-upload-cloud-line" />
                <span>Enviar vídeos do computador</span>
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleVideoUpload}
                  className="hidden"
                />
              </label>

              {/* Add by URL */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newVideoUrl}
                  onChange={(e) => setNewVideoUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addVideoUrl())}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  placeholder="Ou cole a URL do vídeo (YouTube, Vimeo, MP4)..."
                />
                <button
                  type="button"
                  onClick={addVideoUrl}
                  className="px-4 py-2.5 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line" /> Adicionar
                </button>
              </div>
              {form.gallery_videos.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {form.gallery_videos.map((url, idx) => (
                    <div key={idx} className="relative group rounded-lg overflow-hidden aspect-video bg-black">
                      {isVideoUrl(url) ? (
                        <video
                          src={url}
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                          playsInline
                          onClick={(e) => (e.target as HTMLVideoElement).paused ? (e.target as HTMLVideoElement).play() : (e.target as HTMLVideoElement).pause()}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/70">
                          <i className="ri-video-line text-3xl mb-1" />
                          <p className="font-body text-xs text-white/50 truncate max-w-[80%]">{url}</p>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeVideoUrl(idx)}
                        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Remover"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                      <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/50 text-white font-body text-[10px] flex items-center gap-1">
                        <i className="ri-video-line" />
                        {idx + 1}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="px-6 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                {isEditing ? "Salvar alterações" : "Criar evento"}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-6 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-3" />
          <p className="font-body text-sm text-brown/60">Carregando eventos...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center">
          <i className="ri-calendar-line text-4xl text-brown/20 mb-3" />
          <p className="font-body text-brown/60">Nenhum evento cadastrado.</p>
          <button
            onClick={openCreate}
            className="mt-4 text-primary font-body text-sm hover:underline cursor-pointer"
          >
            Criar primeiro evento
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => {
            const isActive = ev.status === "active";
            return (
              <div key={ev.id} className="bg-white rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-4 md:gap-5 items-start">
                <img
                  src={ev.main_image}
                  alt={ev.title}
                  className="w-20 h-20 rounded-xl object-cover shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = FALLBACK_LOGO_URL;
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-display text-base text-brown">{ev.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full font-body text-[11px] uppercase tracking-wide ${isActive ? "bg-primary/10 text-primary" : "bg-red-100 text-red-700"}`}>
                      {isActive ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <p className="font-body text-sm text-brown/60 mb-1">{ev.caption}</p>
                  <div className="flex flex-wrap gap-3 text-xs font-body text-brown/50">
                    {ev.date && <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {ev.date}</span>}
                    {ev.location && <span className="flex items-center gap-1"><i className="ri-map-pin-line" /> {ev.location}</span>}
                    {ev.gallery_images && ev.gallery_images.length > 0 && (
                      <span className="flex items-center gap-1"><i className="ri-image-line" /> {ev.gallery_images.length} foto(s)</span>
                    )}
                    {ev.gallery_videos && ev.gallery_videos.length > 0 && (
                      <span className="flex items-center gap-1"><i className="ri-video-line" /> {ev.gallery_videos.length} vídeo(s)</span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Link
                      to={`/evento/${ev.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-mono text-[11px] bg-beige/50 hover:bg-beige px-2 py-1 rounded-md text-primary transition-colors cursor-pointer"
                      title="Abrir página do evento"
                    >
                      <i className="ri-external-link-line" />
                      /evento/{ev.slug}
                    </Link>
                    <button
                      onClick={(e) => { e.stopPropagation(); copyEventUrl(ev.slug, ev.id); }}
                      className={`flex items-center gap-1 text-[11px] font-body px-2 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                        copiedId === ev.id
                          ? "bg-green-100 text-green-700"
                          : "bg-beige/30 hover:bg-beige text-brown/60 hover:text-brown"
                      }`}
                      title="Copiar link do evento"
                    >
                      <i className={copiedId === ev.id ? "ri-check-line" : "ri-file-copy-line"} />
                      {copiedId === ev.id ? "Copiado!" : "Copiar link"}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    onClick={() => openEdit(ev)}
                    className="px-3 py-2 rounded-lg bg-primary/10 text-primary font-body text-xs hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1" /> Editar
                  </button>
                  <button
                    onClick={() => toggleStatus(ev.id, ev.status)}
                    className={`px-3 py-2 rounded-lg font-body text-xs transition-colors whitespace-nowrap cursor-pointer ${isActive ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                  >
                    {isActive ? "Desativar" : "Ativar"}
                  </button>
                  <button
                    onClick={() => deleteEvent(ev.id)}
                    className="px-3 py-2 rounded-lg bg-white border border-nude text-red-500 font-body text-xs hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}